import { useEffect, useState, useMemo, useRef } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { AddIcon } from "@chakra-ui/icons";
import { batchAssessment, createListProfiles, deleteListProfile, getListProfile, getListProfiles } from "@/services/lists-service";
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertTitle,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
  Input as ChakraInput,
  Select as ChakraSelect,
  Center,
  Spinner,
  Text,
  AlertIcon,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import { Table, TableRowActions, Panel, PageHeading } from "@bemap/ui";
import { constants, formatDate, IBatchAssessment, IListProfile, useAuth } from "@bemap/utils";
import { useTranslation } from "react-i18next";

const ListProfilesList = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();

  const initialListProfile: IListProfile = {
    name: '',
    team_id: user?.currentTeamId || 0,
  };

  const initialBatchAssessments: IBatchAssessment = {
    report_id: 0,
    client_can_unlock: false
  };

  // STATES
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [modalType, setModalType] = useState<"send" | "new" | "delete">();
  const [listProfile, setListProfile] = useState<IListProfile>(initialListProfile);
  const [listProfiles, setListProfiles] = useState<IListProfile[]>([]);
  const [batchAssessments, setBatchAssessments] = useState<IBatchAssessment>(initialBatchAssessments);
  const [updateData, setUpdateData] = useState<string>("");
  const [selectedList, setSelectedList] = useState<number>(0);
  const [file, setFile] = useState<File | null>(null);

  const columns = useMemo<ColumnDef<IListProfile, any>[]>(
    () => [
      {
        accessorFn: (row) => row.name,
        id: "Nome",
        header: t('analyst.groups_table_name'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.created_at,
        id: "Criada em",
        header: t('analyst.companies_table_created'),
        cell: (info) => formatDate(info.getValue()),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row,
        id: "Ações",
        enableColumnFilter: false,
        enableHiding: true,
        header: t('analyst.companies_table_actions'),
        cell: (row) => {
          return (
            <TableRowActions
              newAssesmentAction={() => openBatch(row.getValue().id)}
              viewAction={() => navigate(`${row.getValue().id}`)}
              deleteAction={() => openDeleteDialog(row.getValue().id)}
            />
          );
        },
      },
    ],
    [t, navigate]
  );

  const background = useColorModeValue("transparent", "transparent");
  const textColor = useColorModeValue("#1A202C", "alphaWhite.600");

  useEffect(() => {
    loadListProfiles();
  }, [updateData]);

  function openBatch(id: number) {
    onOpen();
    setModalType('send');
    setSelectedList(id);
  }

  function closeModal() {
    onClose();
    setListProfile(initialListProfile);
    setSelectedList(0);
  }

  async function loadListProfiles() {
    setIsLoading(true);
    try {
      const response = await getListProfiles();
      setListProfiles(response.message);
      setIsLoading(false);
    } catch (error) {
      setListProfiles([]);
      setIsLoading(false);
    }
  }

  async function openDeleteDialog(listProfileId: number) {
    try {
      toast({
        title: t('general.loading'),
        status: 'loading',
        isClosable: true,
      });
      const req = await getListProfile(listProfileId);

      toast.closeAll();
      setListProfile(req.message);
      setModalType("delete");
      onOpen();
    } catch (error) {
      setIsLoading(false);
    }
  }

  const submitBatchAssessments = async () => {
    toast({
      title: t('general.saving'),
      status: 'loading',
      isClosable: true,
    });
    setIsSaving(true);
    try {
      await batchAssessment(selectedList, batchAssessments);
      setUpdateData(new Date().toLocaleString());
      toast.closeAll();
      closeModal();
      toast({
        title: t('analyst.groups_sent_assessments_title'),
        description: t('analyst.groups_sent_assessments_text'),
        status: 'success',
        isClosable: true,
      });
    } catch (error) {
      toast.closeAll();
      toast({
        title: t('general.error'),
        status: 'error',
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteListProfile = async () => {
    toast({
      title: t('general.deleting'),
      status: 'loading',
      isClosable: true,
    });
    try {
      await deleteListProfile(listProfile.id || 0);
      setUpdateData(new Date().toLocaleString());
      closeModal();
    } catch (error) {
      toast.closeAll();
      toast({
        title: t('general.error'),
        status: 'error',
        isClosable: true,
      });
    }
  };

  async function handleChange(event: any) {
    setListProfile({
      ...listProfile,
      [event.target.name]: event.target.value,
    });
  }

  async function handleChangeBatch(event: any) {
    setBatchAssessments({
      ...batchAssessments,
      [event.target.name]: event.target.value,
    });
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
    } else {
      toast({
        title: t('general.invalid_format'),
        description: t('general.invalid_format_csv'),
        status: 'error',
        isClosable: true,
      });
    }
  };

  const handleUpload = async () => {
    setIsSaving(true);
    toast({
      title: t('general.saving'),
      status: 'loading',
      isClosable: true,
    });

    let formData = new FormData();
    formData.append('file', file || new Blob());

    for (const key in listProfile) {
      if (Object.prototype.hasOwnProperty.call(listProfile, key)) {
        const value = listProfile[key as keyof IListProfile];
        if (value !== undefined) {
          formData.append(key, String(value));
        }
      }
    }

    try {
      await createListProfiles(formData);
      toast({
        title: t('general.success'),
        status: 'success',
        isClosable: true,
      });
      setFile(null);
      setIsSaving(false);
      setUpdateData(new Date().toLocaleString());
      closeModal();
    } catch (error) {
      setIsSaving(false);
      toast({
        title: t('general.error'),
        status: 'error',
        isClosable: true,
      });
    }
  };

  function RenderModal() {
    switch (modalType) {
      case "new":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('analyst.groups_new')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Flex direction={"column"}>
                  <FormControl>
                    <FormLabel>{t('analyst.groups_name')}</FormLabel>
                    <ChakraInput
                      color={textColor}
                      background={background}
                      name="name"
                      placeholder="Ex: Formação 2024"
                      onChange={handleChange}
                      value={listProfile.name || ""}
                      required
                    />
                  </FormControl>
                  <FormControl mt={4}>
                    <FormLabel>CSV File</FormLabel>
                    <ChakraInput
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      id="file-upload"
                    />
                  </FormControl>
                  <Alert status='info' mt={4}>
                    <AlertIcon />
                    <AlertTitle fontSize='sm' textAlign='center'>
                      <a href="https://bemap.sfo3.cdn.digitaloceanspaces.com/bemap_import_assessments.xlsx" target="_blank">{t('analyst.groups_alert_title')}</a>
                    </AlertTitle>
                  </Alert>
                  <Text fontSize='sm' color='gray.500' align='center'>
                    {t('analyst.groups_alert_text')}
                  </Text>
                </Flex>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button colorScheme={"blue"} onClick={handleUpload} isLoading={isSaving} isDisabled={!file}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case "send":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('analyst.groups_send')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Flex direction={"column"} gap="20px">
                  <FormControl>
                    <FormLabel>{t('analyst.assesments_new_type')}</FormLabel>
                    <ChakraSelect
                      color={textColor}
                      background={background}
                      name="report_id"
                      defaultValue="default"
                      onChange={handleChangeBatch}
                    >
                      <option value="default" disabled hidden>
                        {t('actions.click_to_select')}
                      </option>
                      {constants.reports.map((report) => {
                        return (
                          <option label={t(report.name)} key={`${report.name}`} value={report.value}>
                            {t(report.name)}
                          </option>
                        )
                      })}
                    </ChakraSelect>
                  </FormControl>
                  <FormControl display='flex' alignItems='center' gap="20px">
                    <Switch
                      id='client_can_unlock'
                      colorScheme={'blue'}
                      onChange={(e) => setBatchAssessments({ ...batchAssessments, client_can_unlock: e.target.checked })}
                    />
                    <FormLabel htmlFor='client_can_unlock' mb='0'>
                      {t('analyst.assesments_new_unlock')}
                    </FormLabel>
                  </FormControl>
                </Flex>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button
                  colorScheme={"blue"}
                  isDisabled={batchAssessments.report_id === 0 || selectedList === 0}
                  onClick={submitBatchAssessments}
                  isLoading={isSaving}
                >
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case 'delete':
        return (
          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={closeModal}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  {t('analyst.groups_delete')}
                </AlertDialogHeader>

                <AlertDialogBody>
                  {t('actions.delete_confirm')}
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeModal}>
                    {t('actions.cancel')}
                  </Button>
                  <Button colorScheme='red' onClick={handleDeleteListProfile} ml={3}>
                    {t('actions.delete_confirm_button')}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        );
    }
  }

  if (isLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <>
      <VStack w="100%" gap={4} align="stretch">
        <PageHeading title={t('analyst.groups')} actions={[
          {
            label: t('analyst.groups_new'),
            icon: <AddIcon />,
            onClick: () => {
              onOpen();
              setModalType("new");
            }
          }
        ]} />
        <Panel>
          <Table
            tableId="clients-lists-table"
            columns={columns}
            data={listProfiles}
          />
        </Panel>
      </VStack>
      {RenderModal()}
    </>
  );
};

export default ListProfilesList; 